/**
 * components/riskTerminal/accounts.js
 *
 * upstox-067 — the accounts the signed-in user may see (GET /upstox/accounts), which one the
 * screen shows, and the one place that adds `?account=<id>` to a request.
 *
 * The account NEVER switches by itself: it is chosen by the user, remembered per user in this
 * browser (a blocked or empty storage falls back cleanly), and falls back to the user's own
 * Upstox account when the remembered one is no longer in his list.
 *
 * `id: null` is the user's own Upstox account when he has none yet (the partner before he
 * signs in to Upstox): requests carry no `account` and the server answers "Upstox not
 * connected" with the sign-in button.
 */

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const STORE_PREFIX = 'rt.account.';

let active = null;                                    // the account id requests carry (null: none)

export function setActiveAccount(id) { active = id == null ? null : id; }
export function getActiveAccount() { return active; }

/** `path` with ?account=<id> (or &account=) for the active account; unchanged when none. */
export function withAccount(path, id = active) {
  if (id == null) return path;
  return path + (path.includes('?') ? '&' : '?') + 'account=' + encodeURIComponent(id);
}

export async function fetchAccounts() {
  try {
    const res = await fetch(`${API}/upstox/accounts`, { credentials: 'include' });
    if (!res.ok) return { ok: false, status: res.status };
    return { ok: true, data: await res.json() };
  } catch (e) {
    return { ok: false, status: 0 };
  }
}

// ── remembered per user, in this browser ─────────────────────────────────

export function readRemembered(userKey) {
  try {
    const raw = window.localStorage.getItem(STORE_PREFIX + userKey);
    if (raw == null) return undefined;
    return raw === 'own' ? null : Number(raw);
  } catch (e) { return undefined; }                   // storage blocked: no memory
}

export function writeRemembered(userKey, id) {
  try { window.localStorage.setItem(STORE_PREFIX + userKey, id == null ? 'own' : String(id)); } catch (e) { /* blocked */ }
}

/**
 * The account to open on: the remembered one when the user may still see it, else his own
 * Upstox account. Returns the entry from `accounts` (never invents one).
 */
export function chooseInitial(accounts, remembered) {
  const list = accounts || [];
  const own = list.find(a => a.broker === 'UPSTOX') || list[0] || null;
  if (remembered === undefined) return own;
  const hit = list.find(a => a.id === remembered);
  return hit || own;
}

// ── how an account is shown ──────────────────────────────────────────────

/** "UPSTOX", "KITE COMMON", "KITE MINE" */
export function accountName(a) {
  if (!a) return '';
  if (a.broker === 'UPSTOX') return 'UPSTOX';
  return String(a.label || a.broker).replace(/\s*·\s*/g, ' ').toUpperCase();
}

/**
 * Three distinct colours, used for nothing else on the screen. upstox-074: the "Deep" set replaces the
 * violet / blue / pink of 067 (the handoff, item 00): ink = the active segment's fill, tint = a
 * hovered inactive segment, id = the client-ID text on the active fill. The Kite accounts take the
 * second and third in id order, so one account is the same colour for both users.
 */
export const ACCOUNT_COLORS = [
  { ink: '#1f3a5f', tint: '#e6ebf2', id: '#c8d2df' },   // UPSTOX (navy)
  { ink: '#1f5f5b', tint: '#e3eeed', id: '#c6dbd9' },   // the first Kite account, Common (teal)
  { ink: '#5b3f2a', tint: '#efe8e1', id: '#dccdbf' },   // the second, Mine (walnut)
];

export function accountColor(accounts, a) {
  if (!a || a.broker === 'UPSTOX') return ACCOUNT_COLORS[0];
  const kites = (accounts || []).filter(x => x.broker === 'KITE').sort((x, y) => x.id - y.id);
  const i = Math.max(0, kites.findIndex(x => x.id === a.id));
  return ACCOUNT_COLORS[1 + (i % 2)];
}

/** The note on the controls that would place an order: the adapter's `can_trade` decides. */
export function ordersNote(account, upstoxNote) {
  if (account && account.can_trade) return '';
  if (account && account.broker === 'KITE') return 'Kite is read-only';
  return upstoxNote;
}
